<template>
	<div class="w-full">
		<RenewPolicyModal :selectedProduct="selectClientPolicy" />
		<CenteredModal
			:show="showDocumentsModal"
			@close="closeViewDocumentsModal"
		>
			<template #default>
				<div class="flex items-start justify-between w-full">
					<h3
						class="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2"
					>
						<svg
							class="w-5 h-5 text-blue-600"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
							/>
						</svg>
						Policy Documents
					</h3>
				</div>

				<div class="card">
					<div
						v-if="policyDocuments && policyDocuments.length"
						class="mt-6"
					>
						<div
							class="bg-white rounded-xl shadow-sm overflow-hidden"
						>
							<ul class="divide-y divide-gray-100">
								<li
									v-for="(doc, index) in policyDocuments"
									:key="index"
									class="group hover:bg-gray-50 transition-colors duration-150"
								>
									<div
										class="px-5 py-4 flex items-center justify-between gap-4"
									>
										<!-- Left side - Document info -->
										<div
											class="flex items-center gap-4 flex-1 min-w-0"
										>
											<div class="flex-shrink-0">
												<div
													class="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center"
												>
													<svg
														class="w-6 h-6 text-blue-600"
														fill="none"
														stroke="currentColor"
														viewBox="0 0 24 24"
													>
														<path
															stroke-linecap="round"
															stroke-linejoin="round"
															stroke-width="2"
															d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
														/>
													</svg>
												</div>
											</div>

											<div class="min-w-0 flex-1">
												<p
													class="text-sm font-medium text-gray-900 truncate"
												>
													{{
														doc.documentName ||
														doc.documentType ||
														doc.fileName ||
														'Policy Document'
													}}
												</p>
												<p
													class="text-xs text-gray-500 mt-0.5"
												>
													{{
														doc.documentType ||
														'PDF'
													}}
													•
													<span
														v-if="doc.uploadedAt"
														>{{
															formatDate(
																doc.uploadedAt,
															)
														}}</span
													>
													<span
														v-else-if="doc.size"
														>{{
															formatFileSize(
																doc.size,
															)
														}}</span
													>
												</p>
											</div>
										</div>

										<!-- Right side - Actions -->
										<div
											class="flex items-center gap-3 flex-shrink-0"
										>
											<a
												v-if="doc.path || doc.link"
												:href="doc.path || doc.link"
												target="_blank"
												rel="noopener noreferrer"
												class="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-green-800 hover:bg-green-700 active:bg-blue-800 rounded-lg transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
											>
												<svg
													class="w-4 h-4"
													fill="none"
													stroke="currentColor"
													viewBox="0 0 24 24"
												>
													<path
														stroke-linecap="round"
														stroke-linejoin="round"
														stroke-width="2"
														d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
													/>
												</svg>
												View
											</a>

											<span
												v-else
												class="text-sm text-gray-500 italic"
											>
												Not available
											</span>
										</div>
									</div>
								</li>
							</ul>
						</div>

						<!-- Optional helpful text -->
						<p class="mt-3 text-sm text-gray-500 text-center">
							Click "View" to open each document in a new tab
						</p>
					</div>

					<div
						v-else
						class="mt-6 p-8 bg-gray-50 border border-dashed border-gray-300 rounded-xl text-center"
					>
						<svg
							class="mx-auto h-12 w-12 text-gray-400"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
							/>
						</svg>
						<h3 class="mt-4 text-lg font-medium text-gray-900">
							No documents available
						</h3>
						<p class="mt-1 text-sm text-gray-500">
							Policy documents will appear here once processed.
						</p>
					</div>
				</div>
			</template>
		</CenteredModal>
		<ViewInvoice />
		<InvoicePayment />
		<!-- Header -->
		<div class="mb-6 w-full">
			<h1
				class="text-2xl lg:text-3xl font-bold mb-4 text-text-primary tracking-tight"
			>
				Policy Details
			</h1>
			<div class="flex items-center w-full justify-between mb-2">
				<div class="flex items-center gap-3 w-full mb-4">
					<button
						v-if="
							selectClientPolicy &&
							selectClientPolicy[0] &&
							selectClientPolicy[0].policyStatus === 'ACTIVE'
						"
						@click="openRenewPolicyModal()"
						class="inline-flex items-center w-fit px-4 py-2 text-sm font-semibold text-white bg-accent hover:bg-primary-light rounded-lg transition-all duration-200 shadow-sm hover:shadow-md active:scale-95"
					>
						<svg
							class="w-4 h-4 mr-2"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
							/>
						</svg>
						Renew Policy
					</button>

					<button
						v-if="selectClientPolicy && selectClientPolicy[0]"
						@click="openViewDocumentsModal(selectClientPolicy[0])"
						class="inline-flex items-center w-fit px-3 py-2 text-sm font-semibold text-white bg-primary hover:bg-primary-light rounded-lg transition-all duration-200 shadow-sm hover:shadow-md active:scale-95"
					>
						<svg
							class="w-4 h-4 mr-2"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M12 4v16m8-8H4"
							/>
						</svg>
						View Documents
					</button>
					<!-- <button
            @click="openSampleDocs"
            class="inline-flex items-center px-3 py-2 text-sm font-medium text-primary bg-white border border-border rounded-lg transition-all duration-200 hover:shadow-sm"
            title="Preview sample documents"
          >
            Preview
          </button> -->
					<button
						v-if="selectClientPolicy[0].policyStatus === 'ACTIVE'"
						@click="openReviewModal(selectClientPolicy[0])"
						class="w-fit inline-flex items-center justify-center px-3 py-2 text-xs lg:text-sm font-semibold rounded-lg text-white bg-green-700 hover:bg-brand-cyan/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-cyan transition-all duration-200 shadow-sm hover:shadow-md active:scale-95"
					>
						<svg
							class="w-3.5 h-3.5 lg:w-4 lg:h-4 mr-1.5"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.783-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
							/>
						</svg>
						Leave Review
					</button>
				</div>
			</div>
			<p class="text-sm lg:text-base text-text-secondary">
				Comprehensive overview of your policy information
			</p>
		</div>

		<!-- Policy Content -->

		<div
			v-if="selectClientPolicy && selectClientPolicy.length"
			class="max-w-full mx-auto p-4 lg:p-8 space-y-6"
		>
			<div
				v-for="policy in selectClientPolicy"
				:key="policy.policyNumber"
				class="space-y-8"
			>
				<div
					class="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-200 pb-6"
				>
					<div>
						<p
							class="text-slate-500 text-sm mt-1 font-mono uppercase tracking-wider"
						>
							{{ policy.policyNumber }}
						</p>
					</div>
					<div class="flex items-center gap-3">
						<span
							class="text-xs font-medium text-gray-800 uppercase tracking-widest"
							>Status</span
						>
						<span
							:class="[
								'px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide border',
								policy.policyStatus === 'ACTIVE'
									? 'bg-green-50 text-green-700 border-emerald-100'
									: 'bg-red-50 text-red-700 border-rose-100',
							]"
						>
							{{ policy.policyStatus }}
						</span>
					</div>
				</div>

				<div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
					<div class="lg:col-span-2 space-y-8">
						<div class="grid grid-cols-1 md:grid-cols-2 gap-6">
							<section
								class="bg-white p-6 rounded-xl border border-slate-200 shadow-sm transition-hover hover:shadow-md"
							>
								<div
									class="flex items-center gap-2 mb-6 text-slate-800"
								>
									<div
										class="p-2 bg-slate-50 rounded-lg text-blue-600"
									>
										<svg
											class="w-5 h-5"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
											/>
										</svg>
									</div>
									<h3 class="font-bold tracking-tight">
										Client Information
									</h3>
								</div>

								<div
									v-for="(
										client, index
									) in policy.client_details"
									:key="index"
									class="space-y-4"
								>
									<div class="group">
										<p
											class="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1"
										>
											Full Name
										</p>
										<p
											class="text-sm font-semibold text-slate-800"
										>
											{{ client.names.firstName }}
											{{ client.names.lastName }}
										</p>
									</div>
									<div class="group">
										<p
											class="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1"
										>
											Email Address
										</p>
										<a
											:href="`mailto:${client.email}`"
											class="text-sm font-medium text-blue-600 hover:text-blue-700 underline underline-offset-4 decoration-blue-200"
										>
											{{ client.email }}
										</a>
									</div>
									<div class="group">
										<p
											class="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1"
										>
											KRA PIN
										</p>
										<p
											class="text-sm font-mono font-medium text-slate-700"
										>
											{{ client.kraPin }}
										</p>
									</div>
								</div>
							</section>

							<section
								class="bg-white p-6 rounded-xl border border-slate-200 shadow-sm"
							>
								<div
									class="flex items-center gap-2 mb-6 text-slate-800"
								>
									<div
										class="p-2 bg-slate-50 rounded-lg text-indigo-600"
									>
										<svg
											class="w-5 h-5"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
											/>
										</svg>
									</div>
									<h3 class="font-bold tracking-tight">
										Policy Metadata
									</h3>
								</div>
								<div class="space-y-4">
									<div
										class="flex justify-between items-center border-b border-slate-50 pb-2"
									>
										<span
											class="text-[11px] font-bold text-slate-400 uppercase tracking-wider"
											>Type</span
										>
										<span
											class="text-sm font-semibold text-slate-700"
											>{{ policy.policyType }}</span
										>
									</div>
									<div
										class="flex justify-between items-center border-b border-slate-50 pb-2"
									>
										<span
											class="text-[11px] font-bold text-slate-400 uppercase tracking-wider"
											>Channel</span
										>
										<span
											class="text-sm font-semibold text-slate-700"
											>{{ policy.quotationChannel }}</span
										>
									</div>
									<div
										class="flex justify-between items-center pb-2"
									>
										<span
											class="text-[11px] font-bold text-slate-400 uppercase tracking-wider"
											>Created</span
										>
										<span
											class="text-sm font-semibold text-slate-700"
											>{{
												formatDate(policy.createdAt)
											}}</span
										>
									</div>
								</div>
							</section>
						</div>

						<section
							class="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden"
						>
							<div
								class="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center"
							>
								<h3
									class="font-bold text-slate-800 tracking-tight flex items-center gap-2"
								>
									<i
										class="fas fa-shield-alt text-slate-400"
									></i>
									Risk Assessment
								</h3>
								<span
									class="text-[10px] bg-slate-200 text-slate-600 px-2 py-0.5 rounded font-bold uppercase"
									>Details</span
								>
							</div>
							<div class="p-0">
								<table class="w-full text-left border-collapse">
									<thead>
										<tr
											class="bg-slate-50/30 text-[11px] uppercase tracking-widest text-slate-400"
										>
											<th class="px-6 py-3 font-bold">
												Reference
											</th>
											<th class="px-6 py-3 font-bold">
												Risk Type
											</th>
											<th
												class="px-6 py-3 font-bold text-right"
											>
												Sum Insured
											</th>
										</tr>
									</thead>
									<tbody class="divide-y divide-slate-100">
										<tr
											v-for="risk in policy.risks"
											:key="risk.riskRef"
											class="hover:bg-slate-50/50 transition-colors"
										>
											<td
												class="px-6 py-4 text-sm font-mono text-slate-500"
											>
												{{ risk.riskRef }}
											</td>
											<td
												class="px-6 py-4 text-sm font-semibold text-slate-700"
											>
												{{ risk.riskType }}
											</td>
											<td
												class="px-6 py-4 text-sm font-bold text-slate-900 text-right"
											>
												KES
												{{ formatCurrency(risk.value) }}
											</td>
										</tr>
									</tbody>
								</table>
							</div>
						</section>
					</div>

					<div class="space-y-8">
						<section
							v-if="policy.quotationPremium"
							class="bg-primary rounded-2xl p-6 text-white shadow-xl shadow-blue-900/10"
						>
							<h3
								class="text-sm font-bold text-blue-400 uppercase tracking-[0.2em] mb-6"
							>
								Financial Summary
							</h3>

							<div class="space-y-4">
								<div class="flex justify-between text-sm">
									<span class="text-gray-400"
										>Base Premium</span
									>
									<span class="font-medium"
										>KES
										{{
											formatCurrency(
												policy.quotationPremium
													.basePremium,
											)
										}}</span
									>
								</div>
								<div class="flex justify-between text-sm">
									<span class="text-gray-400"
										>Taxes & Levies</span
									>
									<span class="font-medium"
										>KES
										{{
											formatCurrency(
												policy.quotationPremium
													.totalCharges,
											)
										}}</span
									>
								</div>
								<div
									class="pt-4 border-t border-slate-800 mt-4 flex justify-between items-end"
								>
									<div>
										<p
											class="text-[10px] font-bold text-blue-400 uppercase tracking-widest mb-1"
										>
											Total Payable
										</p>
										<p class="text-3xl font-light">
											KES
											{{
												formatCurrency(
													policy.quotationPremium
														.totalPremium,
												)
											}}
										</p>
									</div>
									<div
										v-if="policy.policyStatus === 'ACTIVE'"
										class="mb-1"
									>
										<i
											class="fas fa-check-circle text-emerald-400"
										></i>
									</div>
								</div>
							</div>
						</section>

						<section
							class="bg-white p-6 rounded-xl border border-slate-200 shadow-sm"
						>
							<h3
								class="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-6"
							>
								Provider Information
							</h3>
							<div
								v-for="product in policy.product_details"
								:key="product.productSystemRef"
								class="space-y-6"
							>
								<div>
									<p
										class="text-xs font-bold text-gray-800 mb-1"
									>
										{{ product.productName }}
									</p>
									<div
										v-for="un in policy.underwriter"
										:key="un.name"
										class="mt-4 p-3 bg-slate-50 rounded-lg"
									>
										<p
											class="text-xs font-bold text-slate-500 uppercase tracking-tighter mb-1"
										>
											Underwriter
										</p>
										<p
											class="text-sm font-semibold text-slate-800"
										>
											{{ un.name }}
										</p>
										<div
											class="mt-2 flex items-start gap-2 text-slate-500"
										>
											<i
												class="fas fa-map-marker-alt mt-1 text-xs"
											></i>
											<p
												class="text-[12px] leading-relaxed"
											>
												{{ un.address }}
											</p>
										</div>
									</div>
								</div>
							</div>
						</section>
					</div>
				</div>

				<ReviewModal
					:isOpen="showReviewModal"
					@close="showReviewModal = false"
					@submit="handleReviewSubmit"
				/>
			</div>
		</div>

		<!-- Empty State -->
		<div
			v-else
			class="text-center py-12 bg-white border border-border rounded-lg"
		>
			<div
				class="inline-flex items-center justify-center w-16 h-16 rounded-lg bg-surface mb-4"
			>
				<svg
					class="w-8 h-8 text-text-secondary"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
					/>
				</svg>
			</div>
			<h3 class="text-lg font-semibold text-text-primary mb-2">
				No Policy Details Found
			</h3>
			<p class="text-sm text-text-secondary mb-6">
				The policy information you're looking for is not available.
			</p>
			<button
				@click="$router.push({ name: 'NewPortalUserProducts' })"
				class="inline-flex items-center px-5 py-2.5 text-sm font-semibold text-white bg-primary hover:bg-primary-light rounded-lg transition-all duration-200 shadow-sm hover:shadow-md active:scale-95"
			>
				<svg
					class="w-4 h-4 mr-2"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M10 19l-7-7m0 0l7-7m-7 7h18"
					/>
				</svg>
				Back to Policies
			</button>
		</div>
	</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import RenewPolicyModal from '../../newportal/templates/others/RenewPolicyModal.vue';
import CenteredModal from '../../newportal/templates/others/centered-modal.vue';
import ReviewModal from '../../insurance/motor/Motor_private/components/ReviewModal.vue';
export default {
	name: 'ViewPolicy',

	components: {
		RenewPolicyModal,
		CenteredModal,
		ReviewModal,
		ViewInvoice: () => import('./view-invoice'),
		InvoicePayment: () => import('./invoice-payment.vue'),
	},

	data() {
		return {
			showDocumentsModal: false,
			activePolicyForDocs: null,
			showReviewModal: false,
			selectedPolicy: null,
		};
	},

	computed: {
		// map store getters
		...mapGetters('quotations', ['selectClientPolicy']),
		...mapGetters([
			'current',
			'loading',
			'Product',
			'downloads',
			'showClaimsModal',
			'showClaimCancelationModal',
			'showPolicyDocuments',
			'claimFields',
			'showInvoice',
			'showMakePayment',
			'showRenewPolicyModal',
		]),

		// derived list of documents (falling back to store downloads)
		policyDocuments() {
			// const policy = this.activePolicyForDocs || (this.selectClientPolicy && this.selectClientPolicy[0]);
			// if (!policy) return this.downloads || [];
			// return policy.documents || policy.policyDocuments || policy.attachments || this.downloads || [];
			const documents = this.selectClientPolicy[0].policyDocuments;
			return documents;
		},
	},

	methods: {
		...mapActions('product', ['setProduct']),
		openRenewPolicyModal() {
			this.$store.commit('showRenewPolicyModal', true);
		},

		openSampleDocs() {
			this.activePolicyForDocs = null;
			console.log('openSampleDocs called');
			this.showDocumentsModal = true;
		},
		openReviewModal(policy) {
			this.setProduct(policy);
			this.showReviewModal = true;
		},

		handleReviewSubmit({ rating, message }) {
			console.log('Review submitted for policy:', {
				policy: this.selectedPolicy,
				rating,
				message,
			});
			this.$emit('notifications:show', {
				type: 'success',
				title: 'Review Submitted',
				message: 'Thank you for your feedback!',
				duration: 3000,
			});
			this.showReviewModal = false;
			this.selectedPolicy = null;
		},

		openViewDocumentsModal(policy) {
			this.activePolicyForDocs =
				policy ||
				(this.selectClientPolicy && this.selectClientPolicy[0]);
			console.log(
				'openViewDocumentsModal called, activePolicyForDocs:',
				this.activePolicyForDocs,
			);
			this.showDocumentsModal = true;
			console.log('showDocumentsModal set to', this.showDocumentsModal);
		},

		closeViewDocumentsModal() {
			console.log('closeViewDocumentsModal called');
			this.showDocumentsModal = false;
			this.activePolicyForDocs = null;
			console.log('showDocumentsModal set to', this.showDocumentsModal);
		},

		formatDate(dateString) {
			if (!dateString) return 'N/A';
			const date = new Date(dateString);
			return date.toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'long',
				day: 'numeric',
				hour: '2-digit',
				minute: '2-digit',
			});
		},

		formatCurrency(amount) {
			if (amount === null || amount === undefined) return '0.00';
			return parseFloat(amount).toLocaleString('en-US', {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			});
		},
	},

	mounted() {
		if (typeof Event !== 'undefined' && Event && Event.$emit)
			Event.$emit('layout:loading:stop');
		console.log('==policydetails', this.selectClientPolicy);
	},
};
</script>

<style scoped>
/* Focus styles for accessibility */
button:focus-visible,
a:focus-visible {
	outline: 2px solid #1d4ed8; /* explicit primary-blue fallback */
	outline-offset: 2px;
	border-radius: 0.375rem;
}

/* Smooth transitions */
.transition-all {
	transition-property: all;
	transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
	transition-duration: 300ms;
}

/* Responsive adjustments */
@media (max-width: 640px) {
	.text-3xl {
		font-size: 1.875rem;
		line-height: 2.25rem;
	}
}

/* Print styles */
@media print {
	.bg-primary,
	.bg-brand-cyan,
	.bg-accent,
	.bg-green-600,
	.bg-primary-light {
		background: #1d4ed8 !important; /* fallback */
		-webkit-print-color-adjust: exact;
		print-color-adjust: exact;
	}

	.shadow-sm,
	.shadow-md {
		box-shadow: none !important;
	}

	button {
		display: none !important;
	}
}

/* High contrast support */
@media (prefers-contrast: more) {
	.border-border {
		border-color: #000;
	}

	.text-text-primary {
		color: #000;
	}

	.text-text-secondary {
		color: #333;
	}
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
	.transition-all,
	.hover\:shadow-md:hover,
	.active\:scale-95:active {
		transition: none !important;
		transform: none !important;
	}
}
</style>

<!-- end file -->
